import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { RequestService } from '../request.service';
import {
  BandejaAprobacionOrdenCompraDTO,
  BandejaAprobacionOrdenCompraResponseDTO,
  ContadorAprobacionOrdenCompraDTO,
  ContadorSolicitudesAprobacionDTO,
  DetalleAprobacionOrdenCompraDTO,
  DetalleAprobacionOrdenCompraResponseDTO,
  FiltrosAprobacionOrdenCompraDTO,
  ResultadoDecisionAprobacionOrdenCompraDTO,
  ResultadoDecisionAprobacionOrdenCompraResponseDTO,
} from './DTO/aprobacion-orden-compra.dto';

const CONTADOR_VACIO: ContadorAprobacionOrdenCompraDTO = {
  pendientes: 0,
  enRevisionAsignadas: 0,
  totalAccionable: 0,
};

@Injectable({ providedIn: 'root' })
export class AprobacionOrdenCompraWorkspaceService {
  private readonly baseUrl =
    `${environment.apiUrlV2}/workspace/cuentas-por-pagar/aprobaciones-orden-compra`;
  // Contador de todos los tipos de solicitud que el usuario puede ver.
  private readonly contadorUrl =
    `${environment.apiUrlV2}/workspace/cuentas-por-pagar/aprobaciones/contador`;
  private contadorSubject = new BehaviorSubject<ContadorAprobacionOrdenCompraDTO>(
    CONTADOR_VACIO
  );
  private pollingId: any = null;

  readonly contador$ = this.contadorSubject.asObservable();

  constructor(
    private http: HttpClient,
    private requestServ: RequestService
  ) {}

  /**
   * Construye los headers V2 de la sesión; las consultas periódicas omiten el
   * overlay global para no interrumpir el trabajo del usuario.
   */
  private construirHeaders(silencioso = false): HttpHeaders {
    let headers = new HttpHeaders({ token: this.requestServ.master.apiKey });
    if (silencioso) headers = headers.set('X-Skip-Loading', 'true');
    return headers;
  }

  /**
   * Normaliza errores de crearRespuesta y fallos HTTP para que los componentes
   * muestren el mensaje de negocio emitido por el backend.
   */
  private obtenerMensajeError(error: any, respaldo: string): string {
    return error?.error?.respuesta || error?.message || respaldo;
  }

  /**
   * Consulta la bandeja paginada y solo envía filtros con valor; el backend
   * conserva los defaults y aplica el scope de bodega del JWT.
   */
  async listar(
    filtros: FiltrosAprobacionOrdenCompraDTO
  ): Promise<BandejaAprobacionOrdenCompraDTO> {
    let params = new HttpParams();
    if (Number(filtros.page) > 0) {
      params = params.set('page', String(filtros.page));
    }
    if (Number(filtros.limit) > 0) {
      params = params.set('limit', String(filtros.limit));
    }
    if (filtros.q?.trim()) params = params.set('q', filtros.q.trim());
    if (Number(filtros.idEstado) > 0) {
      params = params.set('idEstado', String(filtros.idEstado));
    }
    if (Number(filtros.idProveedor) > 0) {
      params = params.set('idProveedor', String(filtros.idProveedor));
    }
    if (Number(filtros.idSucursalBodega) > 0) {
      params = params.set(
        'idSucursalBodega',
        String(filtros.idSucursalBodega)
      );
    }
    if (Number(filtros.idUsuarioSolicitante) > 0) {
      params = params.set(
        'idUsuarioSolicitante',
        String(filtros.idUsuarioSolicitante)
      );
    }
    if (filtros.tipoOperacion) {
      params = params.set('tipoOperacion', filtros.tipoOperacion);
    }
    if (filtros.fechaDesde) {
      params = params.set('fechaDesde', filtros.fechaDesde);
    }
    if (filtros.fechaHasta) {
      params = params.set('fechaHasta', filtros.fechaHasta);
    }

    try {
      const response = await this.http
        .get<BandejaAprobacionOrdenCompraResponseDTO>(
          `${this.baseUrl}/listar`,
          { headers: this.construirHeaders(), params }
        )
        .toPromise();
      if (response.error || typeof response.respuesta === 'string') {
        throw new Error(
          typeof response.respuesta === 'string'
            ? response.respuesta
            : 'Error al consultar las solicitudes de aprobación'
        );
      }
      return response.respuesta as BandejaAprobacionOrdenCompraDTO;
    } catch (error) {
      throw new Error(
        this.obtenerMensajeError(
          error,
          'No se pudieron consultar las solicitudes de aprobación'
        )
      );
    }
  }

  /**
   * Obtiene el expediente readonly completo dentro del scope autorizado; sus
   * snapshots son la fuente mostrada al revisor para decidir.
   */
  async obtenerDetalle(
    idSolicitudAprobacion: number
  ): Promise<DetalleAprobacionOrdenCompraDTO> {
    try {
      const response = await this.http
        .get<DetalleAprobacionOrdenCompraResponseDTO>(
          `${this.baseUrl}/detalle/${idSolicitudAprobacion}`,
          { headers: this.construirHeaders() }
        )
        .toPromise();
      if (response.error || typeof response.respuesta === 'string') {
        throw new Error(
          typeof response.respuesta === 'string'
            ? response.respuesta
            : 'Error al consultar el expediente de aprobación'
        );
      }
      return response.respuesta as DetalleAprobacionOrdenCompraDTO;
    } catch (error) {
      throw new Error(
        this.obtenerMensajeError(
          error,
          'No se pudo consultar el expediente de aprobación'
        )
      );
    }
  }

  /**
   * Actualiza el contador combinado (costo de OC y devoluciones) entregado por
   * SQL y publica la suma para el sidebar y la bandeja; un fallo silencioso
   * conserva el valor previo.
   */
  async actualizarContador(
    silencioso = true
  ): Promise<ContadorAprobacionOrdenCompraDTO> {
    try {
      const response = await this.http
        .get<{ error: boolean; respuesta: ContadorSolicitudesAprobacionDTO | string }>(
          this.contadorUrl,
          { headers: this.construirHeaders(silencioso) }
        )
        .toPromise();
      if (response.error || typeof response.respuesta === 'string') {
        throw new Error(
          typeof response.respuesta === 'string'
            ? response.respuesta
            : 'Error al contar solicitudes pendientes'
        );
      }
      const combinado = response.respuesta as ContadorSolicitudesAprobacionDTO;
      const tipos = [combinado.costoOc, combinado.devolucion].filter(Boolean);
      const contador: ContadorAprobacionOrdenCompraDTO = {
        pendientes: tipos.reduce((total, tipo) => total + Number(tipo.pendientes), 0),
        enRevisionAsignadas: tipos.reduce((total, tipo) => total + Number(tipo.enRevisionAsignadas), 0),
        totalAccionable: Number(combinado.totalAccionable),
      };
      this.contadorSubject.next(contador);
      return contador;
    } catch (error) {
      if (silencioso) return this.contadorSubject.value;
      throw new Error(
        this.obtenerMensajeError(
          error,
          'No se pudo actualizar el contador de aprobaciones'
        )
      );
    }
  }

  /**
   * Inicia una sola recuperación periódica por sesión; la API sigue siendo la
   * fuente de verdad y el primer conteo se ejecuta inmediatamente.
   */
  iniciarPolling(intervaloMs = 60000): void {
    if (this.pollingId) return;
    this.actualizarContador(true);
    this.pollingId = setInterval(
      () => this.actualizarContador(true),
      intervaloMs
    );
  }

  /**
   * Libera el intervalo al destruir el layout autenticado y reinicia el badge
   * para no conservar datos de otra sesión o empresa.
   */
  detenerPolling(): void {
    if (this.pollingId) clearInterval(this.pollingId);
    this.pollingId = null;
    this.contadorSubject.next(CONTADOR_VACIO);
  }

  /**
   * Asigna bajo lock la solicitud pendiente al usuario autenticado; el backend
   * vuelve a validar elegibilidad, scope y que no sea el solicitante.
   */
  async tomar(
    idSolicitudAprobacion: number
  ): Promise<ResultadoDecisionAprobacionOrdenCompraDTO> {
    return this.ejecutarAccion(
      'tomar',
      idSolicitudAprobacion,
      {},
      'No se pudo tomar la solicitud'
    );
  }

  /**
   * Aprueba la propuesta congelada con la observación del revisor; la aplicación
   * de OC, detalles e ICE permanece dentro de la transacción backend.
   */
  async aprobar(
    idSolicitudAprobacion: number,
    observacionDecision: string
  ): Promise<ResultadoDecisionAprobacionOrdenCompraDTO> {
    return this.ejecutarAccion(
      'aprobar',
      idSolicitudAprobacion,
      { observacionDecision },
      'No se pudo aprobar la solicitud'
    );
  }

  /**
   * Rechaza la propuesta completa sin alterar la OC vigente y conserva la
   * explicación obligatoria como parte de la decisión auditada.
   */
  async rechazar(
    idSolicitudAprobacion: number,
    observacionDecision: string
  ): Promise<ResultadoDecisionAprobacionOrdenCompraDTO> {
    return this.ejecutarAccion(
      'rechazar',
      idSolicitudAprobacion,
      { observacionDecision },
      'No se pudo rechazar la solicitud'
    );
  }

  /**
   * Cancela una solicitud activa con el motivo indicado; el backend diferencia
   * propiedad, permiso crítico y efectos entre creación y propuesta de edición.
   */
  async cancelar(
    idSolicitudAprobacion: number,
    motivoCancelacion: string
  ): Promise<ResultadoDecisionAprobacionOrdenCompraDTO> {
    return this.ejecutarAccion(
      'cancelar',
      idSolicitudAprobacion,
      { motivoCancelacion },
      'No se pudo cancelar la solicitud'
    );
  }

  /**
   * Comparte el contrato POST de las mutaciones y refresca el badge después del
   * commit confirmado, sin duplicar manejo de errores entre acciones.
   */
  private async ejecutarAccion(
    accion: string,
    idSolicitudAprobacion: number,
    body: any,
    mensajeRespaldo: string
  ): Promise<ResultadoDecisionAprobacionOrdenCompraDTO> {
    try {
      const response = await this.http
        .post<ResultadoDecisionAprobacionOrdenCompraResponseDTO>(
          `${this.baseUrl}/${accion}/${idSolicitudAprobacion}`,
          body,
          { headers: this.construirHeaders() }
        )
        .toPromise();
      if (response.error || typeof response.respuesta === 'string') {
        throw new Error(
          typeof response.respuesta === 'string'
            ? response.respuesta
            : mensajeRespaldo
        );
      }
      const resultado =
        response.respuesta as ResultadoDecisionAprobacionOrdenCompraDTO;
      await this.actualizarContador(true);
      return resultado;
    } catch (error) {
      throw new Error(this.obtenerMensajeError(error, mensajeRespaldo));
    }
  }
}
